import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinGeolocationCoordinates } from "~/modules/dtwin/models/DtwinGeolocationCoordinates.interface";

/**
 * Recursively searches for latitude and longitude attributes in an object
 * @param obj - Object to search through
 * @returns An object containing latitude and longitude values, or null if not found
 */
function searchLatLong(obj: unknown): DtwinGeolocationCoordinates | null {
  if (!obj || typeof obj !== "object") {
    return null;
  }

  const record = obj as Record<string, unknown>;

  // Check if the current object has both latitude and longitude
  if ("latitude" in record && "longitude" in record) {
    return {
      latitude: record.latitude as string | number | undefined,
      longitude: record.longitude as string | number | undefined,
    };
  }

  // Recursively search in nested objects
  for (const key in record) {
    if (
      Object.prototype.hasOwnProperty.call(record, key) &&
      typeof record[key] === "object"
    ) {
      const result = searchLatLong(record[key]);
      if (result) {
        return result;
      }
    }
  }

  return null;
}

/**
 * Searches for latitude and longitude coordinates in a Dtwin object.
 *
 * @param dtwin - The Dtwin object to search
 * @returns An object containing latitude and longitude values
 */
export function getDtwinGeolocationCoordinates(
  dtwin: Dtwin,
): DtwinGeolocationCoordinates {
  const result = searchLatLong(dtwin);

  return (
    result ?? {
      latitude: undefined,
      longitude: undefined,
    }
  );
}
